import moment from "moment-timezone";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Day from "../../../../models/ISupplier_Delivery_Schedule_Day";
import IUpsertDeliveryScheduleDayDTO from "../../../../models/DTO/IUpsertDeliveryScheduleDayDTO";

/**
 * Day-of-week is 0=Sunday .. 6=Saturday everywhere in this feature.
 * DAY_LABEL_KEYS are i18n keys (full names) and DAY_ABBR_KEYS are short forms,
 * both translated through OwLabel / FormattedMessage at render time.
 */
export const DAY_LABEL_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export const DAY_ABBR_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * Timezone used when neither the vendor nor the supplier has one. Must match the API's
 * DEFAULT_DELIVERY_TZ (OrderService) so the preview never computes a different cutoff than the
 * server stamps on submit.
 */
export const DEFAULT_DELIVERY_TIMEZONE = "America/New_York";

/** Options for a day-of-week OwSelectOption (value is the 0-6 index). */
export const DAY_OPTIONS = DAY_LABEL_KEYS.map((key, index) => ({ value: index, labelKey: key }));

/**
 * Normalizes a time value for sending to the API: ensures "HH:mm" (drops seconds).
 * Returns null for empty input so all-or-nothing soft-reminder handling stays clean.
 */
export const toApiTime = (value?: string | null): string | null => {
    if (!value) return null;
    const parts = value.split(":");
    if (parts.length < 2) return null;
    const [h, m] = parts;
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

/**
 * Normalizes a time value from the API (tolerates "HH:mm:ss") into the "HH:mm"
 * form that a native <input type="time"> expects.
 */
export const fromApiTime = (value?: string | null): string => {
    if (!value) return "";
    const parts = value.split(":");
    if (parts.length < 2) return "";
    const [h, m] = parts;
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

/** Formats an "HH:mm"/"HH:mm:ss" time as 12-hour (e.g. "6:00 PM"). */
export const format12h = (time?: string | null): string => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours, 10);
    if (isNaN(h)) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${(minutes ?? "00").padStart(2, "0")} ${ampm}`;
};

/** Returns true when the time's minutes are exactly :00. */
export const isOnTheHour = (value?: string | null): boolean => {
    if (!value) return false;
    const parts = value.split(":");
    return parts.length >= 2 && parts[1] === "00";
};

/**
 * Sorts a schedule's days by delivery_day_of_week (Sun-first) for stable display.
 */
export const sortDays = (days?: ISupplier_Delivery_Schedule_Day[]): ISupplier_Delivery_Schedule_Day[] => {
    return [...(days || [])].sort((a, b) => a.delivery_day_of_week - b.delivery_day_of_week);
};

/**
 * Editable form state for a single delivery day. Times are kept in "HH:mm" form
 * for native time inputs; soft reminder is optional and all-or-nothing.
 */
export interface IScheduleDayFormState {
    delivery_day_of_week: number;
    hard_deadline_day_of_week: number;
    hard_deadline_time: string; // "HH:mm"
    send_reminder: boolean;
    soft_reminder_day_of_week: number;
    soft_reminder_time: string; // "HH:mm"
    minimum_order_total: string; // raw input; "" = no minimum (unchanged binary behavior)
}

/** Builds default form state for a brand-new day. */
export const defaultDayFormState = (): IScheduleDayFormState => ({
    delivery_day_of_week: 2, // Tuesday – a sensible non-weekend default
    hard_deadline_day_of_week: 1, // Monday
    hard_deadline_time: "18:00",
    send_reminder: false,
    soft_reminder_day_of_week: 1,
    soft_reminder_time: "12:00",
    minimum_order_total: "",
});

/** Maps an existing API day onto editable form state. */
export const dayToFormState = (day: ISupplier_Delivery_Schedule_Day): IScheduleDayFormState => ({
    delivery_day_of_week: day.delivery_day_of_week,
    hard_deadline_day_of_week: day.hard_deadline_day_of_week,
    hard_deadline_time: fromApiTime(day.hard_deadline_time),
    send_reminder: day.soft_reminder_day_of_week != null && day.soft_reminder_time != null,
    soft_reminder_day_of_week: day.soft_reminder_day_of_week ?? 1,
    soft_reminder_time: fromApiTime(day.soft_reminder_time),
    minimum_order_total: day.minimum_order_total != null ? String(day.minimum_order_total) : "",
});

/**
 * Validation errors for a day form keyed by field. An empty object means valid.
 * Values are i18n keys so the caller can render them via OwLabel/FormattedMessage.
 */
export type ScheduleDayErrors = Partial<
    Record<"hard_deadline_time" | "soft_reminder_time" | "soft_reminder" | "minimum_order_total", string>
>;

/**
 * Enforces the domain rules client-side:
 * - hard deadline time required
 * - soft reminder all-or-nothing (handled here via the send_reminder toggle)
 * - soft reminder minutes must be :00
 */
export const validateScheduleDay = (state: IScheduleDayFormState): ScheduleDayErrors => {
    const errors: ScheduleDayErrors = {};

    if (!state.hard_deadline_time) {
        errors.hard_deadline_time = "field_required";
    }

    if (state.send_reminder) {
        if (!state.soft_reminder_time) {
            errors.soft_reminder = "soft_reminder_all_or_nothing";
        } else if (!isOnTheHour(state.soft_reminder_time)) {
            errors.soft_reminder_time = "reminder_minutes_must_be_zero";
        }
    }

    if (state.minimum_order_total.trim() !== "") {
        const amount = Number(state.minimum_order_total);
        if (!isFinite(amount) || amount < 0) {
            errors.minimum_order_total = "invalid_amount";
        }
    }

    return errors;
};

/**
 * Converts validated form state into the upsert DTO. Soft reminder fields are
 * sent together or both nulled, satisfying the all-or-nothing rule.
 */
export const formStateToDTO = (state: IScheduleDayFormState): IUpsertDeliveryScheduleDayDTO => ({
    delivery_day_of_week: state.delivery_day_of_week,
    hard_deadline_day_of_week: state.hard_deadline_day_of_week,
    hard_deadline_time: toApiTime(state.hard_deadline_time) || "00:00",
    soft_reminder_day_of_week: state.send_reminder ? state.soft_reminder_day_of_week : null,
    soft_reminder_time: state.send_reminder ? toApiTime(state.soft_reminder_time) : null,
    minimum_order_total: state.minimum_order_total.trim() === "" ? null : Number(state.minimum_order_total),
});

/** Human-readable summary of a schedule's delivery days, e.g. "Tue, Thu". */
export const deliveryDaysSummary = (schedule: ISupplier_Delivery_Schedule, abbr: (key: string) => string): string => {
    const days = sortDays(schedule.Days);
    if (!days.length) return "";
    return days.map((d) => abbr(DAY_ABBR_KEYS[d.delivery_day_of_week])).join(", ");
};

/**
 * Whether the delivery-deadlines feature is enabled for a vendor, i.e. it has a
 * delivery schedule assigned. Tolerant of either the scalar id or the populated
 * association being present.
 */
export const isDeliveryDeadlinesEnabled = (
    vendor?: { delivery_schedule_id?: number | null; Delivery_Schedule?: ISupplier_Delivery_Schedule | null } | null
): boolean => !!(vendor?.delivery_schedule_id || vendor?.Delivery_Schedule);

/** A resolved upcoming delivery: which schedule day, the delivery date, and its hard deadline. */
export interface INextDeliveryResult {
    day: ISupplier_Delivery_Schedule_Day;
    deliveryDate: moment.Moment; // start of the delivery day, in the vendor timezone
    hardDeadline: moment.Moment; // deadline datetime, in the vendor timezone
}

/**
 * Computes the soonest achievable delivery across all of a schedule's days.
 *
 * "Achievable" means the day's hard deadline has not yet passed. When the nearest
 * delivery day's deadline is already gone, that cycle is skipped and the next
 * occurrence (up to two cycles out) is used instead. All math is done in the vendor
 * timezone; day-of-week is 0=Sun..6=Sat, matching the rest of this feature.
 *
 * Returns null when there is no schedule / no days, or when no upcoming cycle within
 * the search window still has an open deadline (caller can treat that as "passed").
 */
export const getNextDelivery = (
    schedule: ISupplier_Delivery_Schedule | null | undefined,
    timezone?: string | null,
    now?: moment.Moment
): INextDeliveryResult | null => {
    const days = sortDays(schedule?.Days);
    if (!days.length) return null;

    const tz = timezone || DEFAULT_DELIVERY_TIMEZONE;
    const ref = (now ? now.clone() : moment.tz(tz)).tz(tz);
    const refDay = ref.clone().startOf("day");

    let best: INextDeliveryResult | null = null;

    for (const day of days) {
        // Walk forward up to two cycles so a just-missed deadline rolls to the next week.
        for (let offset = 0; offset <= 2; offset++) {
            // Next occurrence of the delivery day at/after today, plus `offset` weeks.
            const forward = (day.delivery_day_of_week - refDay.day() + 7) % 7;
            const deliveryDate = refDay.clone().add(forward + offset * 7, "days");

            // Hard deadline: the occurrence of hard_deadline_day_of_week at/before the
            // delivery date (within the preceding 7 days), at hard_deadline_time.
            const back = (deliveryDate.day() - day.hard_deadline_day_of_week + 7) % 7;
            const hardDeadline = deliveryDate.clone().subtract(back, "days");
            const [hh, mm] = fromApiTime(day.hard_deadline_time).split(":");
            hardDeadline
                .hour(parseInt(hh, 10) || 0)
                .minute(parseInt(mm, 10) || 0)
                .second(0)
                .millisecond(0);

            // Skip any cycle whose deadline has already passed.
            if (hardDeadline.isBefore(ref)) continue;

            if (!best || deliveryDate.isBefore(best.deliveryDate)) {
                best = { day, deliveryDate, hardDeadline };
            }
            break; // earliest open cycle for this day found
        }
    }

    return best;
};
